import './er-detail-page.scss';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { AppHeader } from '../../../../../components/header/header';
import { amountFormatter, commonButtonMessages, useErDetailLogic, useMyWalletDownloadFileLogic, walletErDetailPageMessages } from 'azshared';
import { useIntl } from 'react-intl';
import { Card, CardSize } from '../../../../../components/card/card';
import { VideoPlayer } from '../../../../../components/video-player/video-player';
import { useGlobalActions } from '../../../../../hooks/global-actions';
import { ErDetailTable } from '../../components/er-detail-table/er-detail-table';
import { PortfolioSummaryChart } from '../../components/portfolio-summary/portfolio-summary';
import { PortfolioAssetClass } from '../../components/portfolio-asset-class/portfolio-asset-class';
import { ErRiskProfile } from './risk-profile/risk-profile';
import { ProductConcentrationChart } from '../../components/product-concentration/product-concentration';
import { ResidualDurationChart } from '../../components/residual-duration-chart/residual-duration-chart';
import { downloadFile } from '../../../../../utils/utils';
import { AppButton } from '../../../../../components/buttons/app-button';

export const ErDetailPage = () => {
  const { formatMessage } = useIntl();
  const globalActions = useGlobalActions();

  const {
    totalAmount,
    products,
    isLoadingWallet,
    isLoadingPortfolioSummaryChart,
    portfolioSummary,
    isLoadingRealEconomyAssetSummary,
    realEconomyAssetSummary,
    isLoadingRealEconomyResidueDuration,
    realEconomyResidueDuration,
    isLoadingRealEconomyRiskProfile,
    realEconomyRiskProfile,
    isLoadingRealEconomyProductsConcentration,
    realEconomyProductsConcentration
  } = useErDetailLogic(globalActions);

  const { onDownloadFile, fileDownloaded, fileDownloadedRequestStatus } = useMyWalletDownloadFileLogic(globalActions);
  const [isPrinting, setIsPrinting] = useState(false);

  useEffect(() => {
    if (isPrinting) {
      if (
        fileDownloadedRequestStatus === 'succeeded' &&
        fileDownloaded?.type === 'realEconomyDetail'
      ) {
        downloadFile(fileDownloaded.name, fileDownloaded.blob);
        setIsPrinting(false);
      } else if (fileDownloadedRequestStatus === 'failed') {
        setIsPrinting(false);
      }
    }
  }, [fileDownloadedRequestStatus, fileDownloaded, isPrinting]);

  const handlePrint = useCallback(() => {
    setIsPrinting(true);
    onDownloadFile('realEconomyDetail', 'pdf');
  }, [onDownloadFile]);

  return (
    <div className='wallet-er-detail-page'>
      <AppHeader />
      <h2>{formatMessage(walletErDetailPageMessages.pageTitle)}</h2>
      <div className='page-content'>
        <Card sizeVariant={CardSize.MAXI}>
          <div id='toPrint' className='card-content'>
            <div className='video-section'>
              <div>
                <h4>{formatMessage(walletErDetailPageMessages.reportVideoTitle)}</h4>
                <p>{formatMessage(walletErDetailPageMessages.videoDescription)}</p>
              </div>
            </div>
            <div id='graphSpacer' />
            <div className='graphs-section'>
              <PortfolioSummaryChart loading={isLoadingPortfolioSummaryChart} summary={portfolioSummary} />
              <PortfolioAssetClass loading={isLoadingRealEconomyAssetSummary} items={realEconomyAssetSummary} />
            </div>
            <hr className='separator' />
            <div className='table-section'>
              <div className='table-section-header'>
                <span>{formatMessage(walletErDetailPageMessages.tableHeader)}</span>
                {!isLoadingWallet && (
                  <span>
                    {formatMessage(walletErDetailPageMessages.tableHeaderAmount)} {totalAmount && amountFormatter(totalAmount)}
                  </span>
                )}
              </div>
              <ErDetailTable products={products} isLoading={isLoadingWallet} />
            </div>
            <div className='page-breaker-div' />
            <hr className={'separator'} />
            <div className={'table-section'}>
              <div className={'table-section-header'}>{formatMessage(walletErDetailPageMessages.riskProfileSectionTitle)}</div>
              <ErRiskProfile profile={realEconomyRiskProfile} isLoading={isLoadingRealEconomyRiskProfile} />
            </div>
            <div className='page-breaker-div' />
            <hr className='separator' />
            <div className='graphs-section'>
              <ProductConcentrationChart loading={isLoadingRealEconomyProductsConcentration} products={realEconomyProductsConcentration?.products ?? []} />
              <div className='right'>
                <ResidualDurationChart residueDuration={realEconomyResidueDuration} isLoading={isLoadingRealEconomyResidueDuration} />
              </div>
            </div>
            <div className='page-actions' data-html2canvas-ignore='true'>
              {!isLoadingWallet && (
                <AppButton
                  label={formatMessage(commonButtonMessages.print)}
                  onClick={handlePrint}
                  loading={isPrinting}
                />
              )}
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
};
